/**
 * When false, the "Add Scene Images" step is hidden in UGC video creation.
 * Backend does not support custom images yet; set to true when ready.
 */
export const UGC_CUSTOM_IMAGES_ENABLED = true;

/** Minimum duration (seconds) for a timeline segment or text overlay when resizing/trimming.
 *  Kept tiny (~3 frames) so users can cut as tight as they want at deep zoom, CapCut-style;
 *  Creatomate handles sub-second durations fine (the backend pins trim_duration and clamps
 *  animation budgets). */
export const MIN_SEGMENT_DURATION_SEC = 0.1;

/**
 * Default/placeholder base video duration (seconds) for the timeline when actual duration is not yet available.
 * Base video is the source of truth; image segments occupy portions of this timeline and don't have to fill it.
 */
export const DEFAULT_BASE_VIDEO_DURATION_SEC = 60;

/** `dataTransfer` type when dragging a media library image onto the UGC timeline (HTML5 DnD). */
export const UGC_LIBRARY_MEDIA_DRAG_TYPE = 'application/x-ugc-media-id';

/** Public SoundHelix example MP3s (algorithmic pieces) — swap for your licensed CDN in production. */
const SH = 'https://www.soundhelix.com/examples/mp3';

export type UGCMusicPresetOption = { id: string; title: string; url: string };

/** Select "Custom URL…" to paste your own HTTPS audio link. */
export const UGC_MUSIC_PRESET_CUSTOM = 'custom';

/** Mood-style labels; each maps to a different demo track so previews feel distinct. */
export const UGC_BACKGROUND_MUSIC_PRESETS: readonly UGCMusicPresetOption[] = [
  { id: 'none', title: 'No background music', url: '' },
  { id: 'upbeat_bright', title: 'Upbeat & bright', url: `${SH}/SoundHelix-Song-1.mp3` },
  { id: 'steady_warm', title: 'Steady & warm', url: `${SH}/SoundHelix-Song-2.mp3` },
  { id: 'bold_driving', title: 'Bold & driving', url: `${SH}/SoundHelix-Song-3.mp3` },
  { id: 'soft_minimal', title: 'Soft & minimal', url: `${SH}/SoundHelix-Song-4.mp3` },
  { id: 'epic_cinematic', title: 'Epic & cinematic', url: `${SH}/SoundHelix-Song-5.mp3` },
  { id: 'playful_light', title: 'Playful & light', url: `${SH}/SoundHelix-Song-6.mp3` },
  { id: 'hopeful_rise', title: 'Hopeful rise', url: `${SH}/SoundHelix-Song-7.mp3` },
  { id: 'cool_modern', title: 'Cool & modern', url: `${SH}/SoundHelix-Song-8.mp3` },
  { id: 'deep_reflective', title: 'Deep & reflective', url: `${SH}/SoundHelix-Song-9.mp3` },
  { id: 'gentle_ambient', title: 'Gentle ambient', url: `${SH}/SoundHelix-Song-10.mp3` },
  { id: 'energetic_pulse', title: 'Energetic pulse', url: `${SH}/SoundHelix-Song-11.mp3` },
  { id: 'smooth_late', title: 'Smooth late-night', url: `${SH}/SoundHelix-Song-12.mp3` },
] as const;

/** No track selected until the user explicitly picks one — music must be opt-in. */
export const UGC_DEFAULT_MUSIC_PRESET_ID: string = 'none';

/**
 * Curated list of Google Fonts the user can pick for text overlays / captions.
 * Names must exactly match the Creatomate-supported family (case + spacing) — the
 * backend allowlist in ugc-pipeline/services.py mirrors this list; keep them in
 * sync. Anything outside the allowlist falls back to "Open Sans" at render time.
 */
export type UGCTextFont = { label: string; family: string };

export const UGC_DEFAULT_TEXT_FONT = 'Open Sans';

export const UGC_TEXT_FONTS: readonly UGCTextFont[] = [
  { label: 'Open Sans',       family: 'Open Sans' },
  { label: 'Roboto',          family: 'Roboto' },
  { label: 'Montserrat',      family: 'Montserrat' },
  { label: 'Poppins',         family: 'Poppins' },
  { label: 'Lato',            family: 'Lato' },
  { label: 'Inter',           family: 'Inter' },
  { label: 'Oswald',          family: 'Oswald' },
  { label: 'Bebas Neue',      family: 'Bebas Neue' },
  { label: 'Anton',           family: 'Anton' },
  { label: 'Archivo Black',   family: 'Archivo Black' },
  { label: 'Playfair Display',family: 'Playfair Display' },
  { label: 'Caveat',          family: 'Caveat' },
] as const;

/**
 * Prepended to the user's topic when calling the chat stream for "Generate" script.
 * Reduces markdown / brief-style output that TTS would read aloud.
 */
export const UGC_AI_SCRIPT_WRITER_PREFIX =
  'Write the spoken voiceover for a short vertical video (Reels / TikTok / Shorts).\n' +
  'Output rules:\n' +
  '- Plain prose only: words the presenter will say out loud.\n' +
  '- Do NOT use markdown (no # headings, **bold**, bullet lists of metadata).\n' +
  '- Do NOT include labels like Length, Style, Format, CTA, or section titles unless they are spoken as part of the ad.\n' +
  '- Short paragraphs or line breaks for pacing; natural punctuation.\n' +
  '- Match the length in the brief. A spoken word runs about 0.4 seconds, so stay inside the word budget given — count the words and cut hard rather than run over.\n\n' +
  'Topic / creative brief:\n';

export type UGCScriptLength = '~15 sec' | '~30 sec' | '~60 sec';

export const UGC_SCRIPT_LENGTHS: readonly UGCScriptLength[] = ['~15 sec', '~30 sec', '~60 sec'] as const;

/** Target video length (seconds) behind each length chip. */
export const UGC_SCRIPT_LENGTH_SECONDS: Record<UGCScriptLength, number> = {
  '~15 sec': 15,
  '~30 sec': 30,
  '~60 sec': 60,
};

/**
 * Speaking pace used to turn a target duration into a word budget for the AI script
 * writer. Avatar TTS (HeyGen / ElevenLabs) reads ~2.4 words/sec at a natural pace;
 * generated scripts tend to overshoot, so the target is biased low and a firmer
 * ceiling (~2.8 w/s) is handed over as the hard cap.
 */
export const UGC_SCRIPT_TARGET_WPS = 2.4;
export const UGC_SCRIPT_MAX_WPS = 2.8;

/** `{ seconds, targetWords, maxWords }` for a chosen length chip (words rounded to 5). */
export const ugcScriptWordBudget = (
  length: UGCScriptLength,
): { seconds: number; targetWords: number; maxWords: number } => {
  const seconds = UGC_SCRIPT_LENGTH_SECONDS[length];
  return {
    seconds,
    targetWords: Math.round((seconds * UGC_SCRIPT_TARGET_WPS) / 5) * 5,
    maxWords: Math.round((seconds * UGC_SCRIPT_MAX_WPS) / 5) * 5,
  };
};

/**
 * Prepended to the finished video script when asking the chat stream to write the
 * social post caption that accompanies the rendered video (the post text, NOT the
 * on-video subtitles). Mirrors how the Social Posts flow generates a caption so the
 * voice/format stays consistent across the product.
 */
export const UGC_AI_CAPTION_WRITER_PREFIX =
  'Write a social media caption for the short vertical video whose voiceover script is below.\n' +
  'Output rules:\n' +
  '- Return ONLY the caption text — no preamble, no quotes, no labels.\n' +
  '- Hook in the first line; a few short, scannable lines; natural punctuation.\n' +
  '- A handful of relevant hashtags on the last line is fine.\n' +
  '- Do NOT use markdown headings or bullet lists.\n\n' +
  'Video script:\n';
